import { ApolloProvider } from 'react-apollo';
import { Provider } from 'react-redux';
import { has, includes, isEmpty, isString, merge, pickBy, snakeCase } from 'lodash';
import Logger from '@sm360/logger-sentry';
import PropTypes from 'prop-types';
import React from 'react';

import {
    ConfigsService as GeneralConfigService,
    ContentZonesService,
    LocaleService,
    UrlService,
    WindowProxy,
} from '@sm360/widget-commons-core-services';
import {
    AccountService,
    EVENTS,
    HistoryService,
    LocalStorageService,
    ReduxClient,
    ApolloClient,
    ReactApolloClient,
} from '@sm360/widget-commons-general';
import { PurchaseMethods, Vehicle } from '@sm360/widget-commons-vehicle';
import TradeIn from '@sm360/widget-trade-in';

import * as ListingUsedActions from 'inventory-listing/actions/listingUsedActions.js';
import { COLANDER_GROUPS, COLANDER_SLUGS } from 'inventory-listing/constants/colanders.js';

import ListingUsedModel from 'inventory-listing/models/ListingUsedModel.js';
import VehicleCriteriaModel from 'inventory-listing/models/VehicleCriteriaModel.js';

import ListingUsedContainer from 'inventory-listing/containers/ListingUsed/ListingUsed.js';
import ListingNewContainer from 'inventory-listing/containers/ListingNew/ListingNew.js';

import en from 'inventory-listing/i18n/en.json';
import fr from 'inventory-listing/i18n/fr.json';
import listingUsedReducer from 'inventory-listing/reducers/listingUsedReducer.js';
import vehiclesReducer from 'inventory-listing/reducers/vehiclesReducer.js';
import {
    fetchMakePriorities,
    mapMakePrioritiesAsVehicleOrders,
} from 'inventory-listing/services/CatalogAPIService/CatalogAPIService';

const { TradeInVehicleActions } = Vehicle;

const {
    PurchaseMethodsActions,
    PurchaseMethodsConstant,
    PurchaseMethodsModel,
    PaymentFrequencyService,
} = PurchaseMethods;

const snakeCaseOrDefault = (str, defaultValue) => (isString(str) && str !== '' ? snakeCase(str) : defaultValue);

class ListingUsedProvider extends React.Component {
    constructor(props) {
        super(props);

        merge(en, Vehicle.Translation.en, PurchaseMethods.Translations.en, TradeIn.Translations.en);
        merge(fr, Vehicle.Translation.fr, PurchaseMethods.Translations.fr, TradeIn.Translations.fr);

        LocaleService.setLocale(props.locale);
        LocaleService.loadTranslations({ en, fr });

        GeneralConfigService.setOrganizationId(props.organizationId);
        GeneralConfigService.setOrganizationUnitId(props.organizationUnitId);
        GeneralConfigService.setOrganizationUnitLocation(props.organizationUnitLocation);
        GeneralConfigService.setWebsiteId(props.websiteId);
        GeneralConfigService.setLocation(props.location);

        this.logger = new Logger(this.constructor.displayName, GeneralConfigService.getLoggerContext());
        if (props.configs && props.configs.source && props.configs.source.image) {
            ContentZonesService.setImageServerBaseUrl(props.configs.source.image);
        }
        ContentZonesService.addContentZones(props.contentZones);

        this.storeRedux = ReduxClient.getOrCreate();
        ReduxClient.addReducers({
            listingUsed: listingUsedReducer,
            vehicles: vehiclesReducer,
            purchaseMethods: PurchaseMethods.PurchaseMethodsReducer,
            tradeInVehicle: Vehicle.TradeInVehicleReducer,
        });

        this.formatProperties = this.formatProperties.bind(this);
        this.getDefaultColanders = this.getDefaultColanders.bind(this);
        this.getColanderList = this.getColanderList.bind(this);
        this.buildVehicleCriteria = this.buildVehicleCriteria.bind(this);
        this.buildModel = this.buildModel.bind(this);
        this.onTradeInCreated = this.onTradeInCreated.bind(this);
        this.checkTradeInVehicleStatus = this.checkTradeInVehicleStatus.bind(this);
        this.onUnload = this.onUnload.bind(this);
        this.initializePageNumber = this.initializePageNumber.bind(this);

        this.state = {
            initialPageNumber: 1,
        };
    }

    componentDidMount() {
        this.initializePageNumber();

        const $window = WindowProxy.get();

        if ($window) {
            $window.addEventListener(EVENTS.TRADEIN_CREATED, this.onTradeInCreated);
            $window.addEventListener('beforeunload', this.onUnload);
        }

        const preferredPurchaseMethod = UrlService.getUrlParameter('preferredPurchaseMethod');
        const preferredPaymentFrequency = UrlService.getUrlParameter('preferredPaymentFrequency');

        const props = this.formatProperties(this.props);
        this.buildModel(props)
            .then(ListingUsedActions.setListingUsed)
            .then(this.storeRedux.dispatch);
        this.storeRedux.dispatch(ListingUsedActions.translateListingUsed());

        if (includes(PurchaseMethodsConstant, preferredPurchaseMethod)) {
            this.storeRedux.dispatch(ListingUsedActions.selectPurchaseMethod(preferredPurchaseMethod));
        }
        if (Number.isFinite(parseInt(preferredPaymentFrequency, 10))) {
            this.storeRedux.dispatch(
                ListingUsedActions.selectPreferredPaymentFrequency(parseInt(preferredPaymentFrequency, 10))
            );
        }

        this.checkTradeInVehicleStatus(true);
    }

    componentWillUnmount() {
        const $window = WindowProxy.get();

        if ($window) {
            $window.removeEventListener(EVENTS.TRADEIN_CREATED, this.onTradeInCreated);
            this.onUnload();
        }
    }

    onUnload() {
        HistoryService.add(WindowProxy.location().href);
    }

    onTradeInCreated() {
        this.checkTradeInVehicleStatus(true);
    }

    getDefaultColanders(props) {
        const { colanders } = props;
        if (!colanders || !colanders.widgetColanderGroups) {
            this.logger.error(`The colanders are invalid [${colanders}]`);
            return [];
        }

        const widgetColanderGroup = colanders.widgetColanderGroups.find(
            (colanderGroup) => snakeCase(colanderGroup.slug) === snakeCase(props.colanderGroupSlug)
        );

        if (widgetColanderGroup) {
            return widgetColanderGroup.colanders;
        }

        this.logger.error(`There is no widgetColanderGroup for the slug [${props.colanderGroupSlug}]`);
        return [];
    }

    getColanderList(props) {
        const { colanders } = props;
        if (!colanders || !colanders.widgetColanderGroups) {
            this.logger.error(`The colanders are invalid [${colanders}]`);
            return [];
        }

        const widgetColanderGroup = colanders.widgetColanderGroups.find(
            (colanderGroup) => snakeCase(colanderGroup.slug) === snakeCase(props.colanderGroupSlug)
        );

        if (widgetColanderGroup) {
            return colanders.widgetColanderGroups;
        }

        this.logger.error(`There is no colanderList for the slug [${props.colanderGroupSlug}]`);
        return [];
    }

    initializePageNumber() {
        this.setState({
            initialPageNumber: Number.parseInt(UrlService.getUrlParameter('page'), 10) || 1,
        });
    }

    checkTradeInVehicleStatus(forceLoad) {
        const state = this.storeRedux.getState();
        if (forceLoad || isEmpty(state.tradeInVehicle) || !state.tradeInVehicle.id) {
            if (AccountService.isAuthenticated()) {
                this.storeRedux.dispatch(TradeInVehicleActions.loadTradeInVehicle(state.tradeInVehicle));
            } else {
                const defaultPurchaseMethods = new PurchaseMethodsModel();
                this.storeRedux.dispatch(PurchaseMethodsActions.setTradeIn(defaultPurchaseMethods.tradeIn));
                this.storeRedux.dispatch(PurchaseMethodsActions.setLien(defaultPurchaseMethods.lien));
            }
        }
    }

    formatProperties(props) {
        const properties = { ...props };
        const { colanderGroupSlug, colanders } = properties;

        if (colanders && colanders.currentWidgetColanderGroup) {
            properties.colanders.currentWidgetColanderGroup.slug = snakeCaseOrDefault(
                colanders.currentWidgetColanderGroup.slug,
                this.constructor.defaultProps.colanderGroupSlug
            );
        }

        if (colanders && colanders.widgetColanderGroups) {
            properties.colanders.widgetColanderGroups = colanders.widgetColanderGroups.map((item) => {
                item.slug = snakeCaseOrDefault(item.slug, this.constructor.defaultProps.colanderGroupSlug);
                return item;
            });
        }

        properties.colanderGroupSlug = snakeCaseOrDefault(
            colanderGroupSlug,
            this.constructor.defaultProps.colanderGroupSlug
        );

        return properties;
    }

    buildVehicleCriteria(props) {
        const context = props.listingContext;
        const drivetrains = UrlService.getUrlParameter('drivetrains');
        const frameStyle = UrlService.getUrlParameter('frameStyleTypes');
        const fuelTypes = UrlService.getUrlParameter('fuelTypes');
        const transmissionTypes = UrlService.getUrlParameter('transmissionTypes');
        const odometerFrom = UrlService.getUrlParameter('odometerFrom');
        const odometerTo = UrlService.getUrlParameter('odometerTo');
        const textQuerySearch = UrlService.getUrlParameter('textQuery');
        const salePriceRangeFrom = UrlService.getUrlParameter('salePriceFrom');
        const salePriceRangeTo = UrlService.getUrlParameter('salePriceTo');
        const yearFrom = UrlService.getFirstUrlParameter(['yearFrom', 'minYear']);
        const yearTo = UrlService.getFirstUrlParameter(['yearTo', 'maxYear']);

        // Here 'colanderSlug' represent the slug of the colander from the context
        const colanderSlug = context && isString(context.colanderSlug) ? context.colanderSlug : COLANDER_SLUGS.USED;

        const onlyBuiltVehicles = isString(props.colanderGroupSlug)
            ? props.colanderGroupSlug === COLANDER_GROUPS.NEW
            : true;

        const yearFromNumber = parseInt(yearFrom, 10);
        const yearToNumber = parseInt(yearTo, 10);
        const yearRange =
            Number.isFinite(yearFromNumber) && Number.isFinite(yearToNumber)
                ? {
                      from: yearFromNumber,
                      to: yearToNumber,
                      type: 'CLOSED',
                  }
                : {};

        const odometerFromNumber = parseInt(odometerFrom, 10);
        const odometerToNumber = parseInt(odometerTo, 10);
        const odometerRange = Number.isFinite(odometerToNumber)
            ? {
                  type: 'CLOSED',
                  from: Number.isFinite(odometerFromNumber) ? odometerFromNumber : 0,
                  to: odometerToNumber,
              }
            : {};

        const priceFromNumber = parseInt(salePriceRangeFrom, 10);
        const priceToNumber = parseInt(salePriceRangeTo, 10);
        const salePriceRange = Number.isFinite(priceToNumber)
            ? {
                  type: 'CLOSED',
                  from: Number.isFinite(priceFromNumber) ? priceFromNumber : 0,
                  to: priceToNumber,
              }
            : {};

        const textQuery = String(textQuerySearch);

        const vehicleCriteria = new VehicleCriteriaModel({
            vehicleStatuses: ['FOR_SALE', 'SOLD', 'VIRTUAL'],
            colanderSlug,
            onlyBuiltVehicles,
            drivetrains: drivetrains ? drivetrains.split(',') : [],
            fuelTypes: fuelTypes ? fuelTypes.split(',') : [],
            frameStyleTypes: frameStyle ? frameStyle.split(',') : [],
            makeIds: context && context.make ? [context.make.id.toString()] : [],
            modelIds: context && context.model ? [context.model.id.toString()] : [],
            transmissionTypes: transmissionTypes ? transmissionTypes.split(',') : [],
            odometerRange,
            yearRange,
            salePriceRange,
            textQuery,
        });

        return vehicleCriteria;
    }

    buildModel(props) {
        const colanders = this.getDefaultColanders(props);
        const colanderList = this.getColanderList(props);
        const vehicleCriteria = this.buildVehicleCriteria(props);
        const urlParams = UrlService.getUrlParameters() || {};

        const localStorageCashDown = parseInt(LocalStorageService.get('cashDown'), 10) || null;
        const initialCashDown =
            has(urlParams, 'initialCashDown') && parseInt(urlParams.initialCashDown, 10)
                ? parseInt(urlParams.initialCashDown, 10)
                : localStorageCashDown;

        const initialTargetPayment =
            has(urlParams, 'initialTargetPayment') && parseInt(urlParams.initialTargetPayment, 10)
                ? parseInt(urlParams.initialTargetPayment, 10)
                : null;

        const maximumTerm =
            has(urlParams, 'maximumTerm') && parseInt(urlParams.maximumTerm, 10)
                ? parseInt(urlParams.maximumTerm, 10)
                : null;

        const frequencyValues = PaymentFrequencyService.getPaymentFrequencyDropDownValues(LocaleService);

        const contentZoneDefaultPaymentType = ContentZonesService.getContentZone(
            'widget.listing.used',
            'default.purchase.method'
        );

        const purchaseMethod = contentZoneDefaultPaymentType && contentZoneDefaultPaymentType.getValue();

        const paymentOptions = new Vehicle.PaymentOptionsModel(
            pickBy({
                purchaseMethod,
                initialCashDown,
                initialTargetPayment,
                maximumTerm,
                frequencyValues,
            })
        );

        const contentZoneDefaultSort = ContentZonesService.getContentZone('widget.listing.used', 'default.sort');
        let defaultSort =
            contentZoneDefaultSort && contentZoneDefaultSort.getValue() && contentZoneDefaultSort.getValue().split('_');
        defaultSort = defaultSort || ['PRICE', 'ASC'];
        let defaultVehicleOrders = [
            {
                orderDirection: defaultSort && defaultSort.pop(),
                vehicleOrderField: defaultSort && defaultSort.join('_'),
            },
        ];

        const listing = {
            colanderGroup: props.colanderGroupSlug,
            colanderList,
            colanders,
            dealerMake: props.dealerMake,
            vehicleCriteria,
            defaultVehicleOrders,
            contextUrl: props.urlTemplate,
            carfaxKey: props.carproofKey,
            vdpUrl: props.vdpUrl,
            paymentOptions,
        };

        // Here we use 'pickBy' to filter truthy values.
        // Falsy values are set inside the model to defaults.
        const listingUsedModel = new ListingUsedModel(pickBy(listing));

        if (props.hasMakePriorityEnabled) {
            return fetchMakePriorities(props.organizationUnitId)
                .then(mapMakePrioritiesAsVehicleOrders)
                .catch(() => {
                    this.logger.error(`Unable to fetch make priorities while building listing`);
                    return [];
                })
                .then((makePriorities) => {
                    defaultVehicleOrders = makePriorities.concat(defaultVehicleOrders);
                    return listingUsedModel.set('defaultVehicleOrders', defaultVehicleOrders);
                });
        }
        return Promise.resolve(listingUsedModel);
    }

    render() {
        let listing = null;
        const props = this.formatProperties(this.props);

        switch (props.colanderGroupSlug) {
            case COLANDER_GROUPS.NEW:
                listing = <ListingNewContainer {...props} pageNumber={this.state.initialPageNumber} />;
                break;
            default:
            case COLANDER_GROUPS.DEFAULT: // eslint-disable-line no-fallthrough
                listing = <ListingUsedContainer {...props} pageNumber={this.state.initialPageNumber} />;
                break;
        }

        return (
            <ApolloProvider client={props.apolloClient}>
                <Provider store={this.storeRedux}>{listing}</Provider>
            </ApolloProvider>
        );
    }
}

ListingUsedProvider.defaultProps = {
    dealerMake: '',
    listingContext: {},
    colanderGroupSlug: COLANDER_GROUPS.DEFAULT,
    listingH1: LocaleService.t('FIND_USED_CAR'),
    carproofKey: '',
    apolloClient: ApolloClient.getOrCreate(),
    configs: null,
    hasMakePriorityEnabled: false,
};

ListingUsedProvider.propTypes = {
    apolloClient: PropTypes.shape(ReactApolloClient),
    locale: PropTypes.string.isRequired,
    websiteId: PropTypes.number.isRequired,
    location: PropTypes.string.isRequired,
    dealerMake: PropTypes.string,
    configs: PropTypes.shape({
        source: PropTypes.shape({
            image: PropTypes.string,
        }),
    }),
    organizationUnitLocation: PropTypes.string.isRequired,
    organizationUnitId: PropTypes.number.isRequired,
    organizationId: PropTypes.number.isRequired,
    contentZones: PropTypes.objectOf(
        PropTypes.arrayOf(
            PropTypes.shape({
                active: PropTypes.bool,
                group: PropTypes.string,
                id: PropTypes.number,
                location: PropTypes.string,
                position: PropTypes.number,
                type: PropTypes.string,
                value: PropTypes.string,
            })
        )
    ).isRequired,
    listingContext: PropTypes.shape({
        // eslint-disable-line react/no-unused-prop-types
        make: PropTypes.shape({
            id: PropTypes.number,
            name: PropTypes.string,
        }),
        model: PropTypes.shape({
            id: PropTypes.number,
            name: PropTypes.string,
        }),
        category: PropTypes.shape({
            id: PropTypes.string,
            name: PropTypes.string,
            description: PropTypes.string,
        }),
        colanderSlug: PropTypes.string,
    }),
    listingH1: PropTypes.string, // eslint-disable-line react/no-unused-prop-types
    urlTemplate: PropTypes.string.isRequired, // eslint-disable-line react/no-unused-prop-types
    vdpUrl: PropTypes.string.isRequired, // eslint-disable-line react/no-unused-prop-types
    colanders: PropTypes.shape({
        // eslint-disable-line react/no-unused-prop-types
        widgetColanderGroups: PropTypes.arrayOf(
            PropTypes.shape({
                label: PropTypes.string,
                slug: PropTypes.string,
            })
        ),
    }).isRequired,
    colanderGroupSlug: PropTypes.string, // eslint-disable-line react/no-unused-prop-types
    carproofKey: PropTypes.string,
    hasMakePriorityEnabled: PropTypes.bool,
};

ListingUsedProvider.displayName = 'ListingUsedProvider';

export default ListingUsedProvider;



// WEBPACK FOOTER //
// ./client/src/ListingUsedProvider.jsx